import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Space, Typography } from "antd";
import React from "react";
import styles from "./style.module.scss";

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  componentStack: string;
}

interface Props {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<Props> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    componentStack: "",
  };

  showError = (
    error: Error,
    componentName: string,
    additionalInfo?: string,
  ) => {
    this.setState({
      hasError: true,
      error: error instanceof Error ? error : new Error(String(error)),
      errorInfo: null,
      componentStack: `Ошибка в ${componentName}${
        additionalInfo ? `: ${additionalInfo}` : ""
      }`,
    });
  };

  componentDidMount() {
    window.onerror = (message, source, lineno, colno, error) => {
      if (String(message).includes("Script error")) {
        return false;
      }

      let componentName = "Неизвестный компонент";
      if (error?.stack) {
        const match = error.stack.match(/at\s+(\w+)\s+\(/);
        if (match) {
          componentName = match[1];
        }
      }

      this.setState({
        hasError: true,
        error: error || new Error(String(message)),
        componentStack: `Ошибка в ${componentName}: ${
          source || "неизвестный источник"
        }:${lineno || "?"}:${colno || "?"}`,
      });

      return false;
    };

    window.onunhandledrejection = (event) => {
      let componentName = "Неизвестный компонент";
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));

      if (error.stack) {
        const match = error.stack.match(/at\s+(\w+)\s+\(/);
        if (match) {
          componentName = match[1];
        }
      }

      this.setState({
        hasError: true,
        error: error,
        componentStack: `Необработанный Promise rejection в ${componentName}`,
      });
    };
  }

  static getDerivedStateFromError(error: any) {
    return {
      hasError: true,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }

  componentDidCatch(error: any, errorInfo: React.ErrorInfo) {
    let componentName = "Неизвестный компонент";
    const errorObj = error instanceof Error ? error : new Error(String(error));

    if (errorObj.stack) {
      const match = errorObj.stack.match(/at\s+(\w+)\s+\(/);
      if (match) {
        componentName = match[1];
      }
    }

    this.setState({
      error: errorObj,
      errorInfo: errorInfo,
      componentStack: `Ошибка в ${componentName}\n${
        errorInfo.componentStack || ""
      }`,
    });
  }

  onClick = () => {
    localStorage.clear();
    window.location.reload();
  };

  getComponentNameFromStack = (stack: string): string => {
    if (!stack) return "Неизвестный компонент";

    const match = stack.match(/at\s+(\w+)\s+\(/);
    if (match && match[1]) {
      return match[1];
    }

    const altMatch = stack.match(/in\s+(\w+)\s+\(/);
    if (altMatch && altMatch[1]) {
      return altMatch[1];
    }

    return "Неизвестный компонент";
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, componentStack } = this.state;
      const errorMessage = error?.message;
      const componentName = this.getComponentNameFromStack(
        componentStack || errorInfo?.componentStack || "",
      );

      return (
        <div className={styles.container}>
          <Space
            orientation="vertical"
            size="medium"
            style={{ width: "100%", maxWidth: "800px" }}
          >
            <div style={{ textAlign: "center" }}>
              <Typography.Title level={3} style={{ color: "#ff4d4f" }}>
                Что-то пошло не так...
              </Typography.Title>
              <Typography.Text type="secondary">
                Пожалуйста, попробуйте перезапустить приложение
              </Typography.Text>
            </div>

            <Alert
              type="error"
              title="Информация об ошибке"
              description={
                <Space
                  orientation="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <div>
                    <Typography.Text code style={{ marginLeft: "8px" }}>
                      {error?.name || "Error"}
                    </Typography.Text>
                  </div>

                  <div>
                    <Typography.Text
                      code
                      style={{ marginLeft: "8px", whiteSpace: "pre-wrap" }}
                    >
                      {errorMessage}
                    </Typography.Text>
                  </div>

                  <div>
                    <Typography.Text code style={{ marginLeft: "8px" }}>
                      {componentName}
                    </Typography.Text>
                  </div>

                  <div>
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "12px",
                        background: "#f5f5f5",
                        borderRadius: "4px",
                        maxHeight: "150px",
                        overflow: "auto",
                      }}
                    >
                      <Typography.Text
                        code
                        style={{ fontSize: "12px", whiteSpace: "pre-wrap" }}
                      >
                        {componentStack || "Нет информации о компоненте"}
                      </Typography.Text>
                    </div>
                  </div>
                </Space>
              }
            />

            <Button
              icon={<ReloadOutlined />}
              onClick={this.onClick}
              size="large"
              block
              style={{ marginTop: "16px" }}
              color="green"
            >
              Перезапустить приложение
            </Button>
          </Space>
        </div>
      );
    }

    return this.props.children;
  }
}
